import { ActionIcon, Group, Menu } from "@mantine/core";
import { Text } from "../Generics/Text";
import {
  IconDotsVertical,
  IconEdit,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import React, { FC, useEffect, useRef, useState } from "react";
import { ProjectDataType } from "@/types/apiTypes";
import { useHomePageContext } from "@/hooks/contextHooks/useHomePageContext";
import { useFetchProjects } from "@/hooks/apiHooks/useFetchProjects";
import { useDeleteProject } from "@/hooks/apiHooks/useDeleteProject";
import { usePageTransition } from "@/hooks/helperHooks/usePageTransition";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";
import styles from "../../css/gen.module.css";

type Props = {
  selectedProject: ProjectDataType | null;
  handleProjectClick: (project: ProjectDataType | null) => void;
  openEditProjectModal: (proj: ProjectDataType) => void;
  setIsCreateProjectModalOpen: (val: boolean) => void;
};

export const GenProjects: FC<Props> = ({
  selectedProject,
  handleProjectClick,
  openEditProjectModal,
  setIsCreateProjectModalOpen,
}) => {
  const transition = usePageTransition();

  const { colors } = useAppColors();

  const { projects, setProjects } = useHomePageContext();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = x - startX;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const { data: projectsData, isLoading } = useFetchProjects();
  const { mutate: deleteProject } = useDeleteProject();

  // console.log("📂 projectsData -> " + JSON.stringify(projectsData));

  useEffect(() => {
    if (projectsData) {
      setProjects(projectsData);
    }
  }, [projectsData, setProjects]);

  const handleDeleteProject = (projectId: string) => {
    deleteProject(projectId);
  };

  if (isLoading || transition) return null;

  return (
    <div
      className="hide-scrollbar"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "1rem",
        padding: "1rem 1rem 0 1rem",
        overflowX: "scroll",
        width: "100%",
      }}
      ref={scrollRef}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseUp}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      {/* 'All' Project Card */}
      <div
        className={styles.projectCard}
        style={{
          backgroundColor: !selectedProject
            ? colors.selected
            : colors.backgroundSubtle,
          borderColor: colors.border,
        }}
        onClick={() => handleProjectClick(null)}
      >
        <Text weight="bold" color={colors.secondary}>
          All Projects
        </Text>
      </div>

      {projects.map((project: any) => (
        <div
          key={project.id}
          className={styles.projectCard}
          style={{
            borderColor: colors.border,
            backgroundColor:
              selectedProject?.id === project.id
                ? colors.selected
                : colors.backgroundSubtle,
          }}
          onClick={() => handleProjectClick(project)}
        >
          <Group justify="space-between">
            <Text weight="bold" color={colors.secondary}>
              {project.name}
            </Text>

            <Menu>
              <Menu.Target>
                <ActionIcon color={colors.secondary}>
                  <IconDotsVertical size={16} color={colors.primary} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconEdit size={14} />}
                  onClick={() => openEditProjectModal(project)}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => handleDeleteProject(project.id)}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          {project.deadline && (
            <div
              style={{
                backgroundColor: colors.secondary,
                color: colors.primary,
                fontSize: "1rem",
                width: "fit-content",
                padding: ".2rem 1rem",
                borderRadius: 6,
              }}
            >
              Deadline: {new Date(project.deadline).toLocaleDateString()}
            </div>
          )}
          {project.description && (
            <div style={{ marginTop: ".5rem" }}>
              <Text size="small" color={colors.secondary}>
                {project.description}
              </Text>
            </div>
          )}
        </div>
      ))}

      {/* Add New Project Card */}
      <div
        className={styles.projectCard}
        style={{
          backgroundColor: colors.backgroundSubtle,
          borderColor: colors.border,
        }}
        onClick={() => setIsCreateProjectModalOpen(true)}
      >
        <Group>
          <IconPlus size={22} color={colors.secondary} />
          <Text weight="bold" color={colors.secondary}>
            Add New Project
          </Text>
        </Group>
      </div>
    </div>
  );
};
